// GbpPostsCard — monthly Google Business Profile post drafts (text EN/SQ/ME + photo set) ready to copy into GBP.
import React, { useEffect, useState } from 'react';
import { Copy, Check, Loader2, Megaphone, RefreshCw, ExternalLink } from 'lucide-react';
import { apiCall, API_URL } from './_shared';

const abs = (u) => (u && !/^https?:/.test(u) ? `${API_URL}${u}` : u);

const LANGS = [['en', 'English'], ['sq', 'Shqip'], ['me', 'Crnogorski']];

const CopyBtn = ({ text, testId }) => {
  const [ok, setOk] = useState(false);
  const copy = () => navigator.clipboard?.writeText(text).then(() => { setOk(true); setTimeout(() => setOk(false), 1800); }).catch(() => {});
  return (
    <button type="button" onClick={copy} className="inline-flex items-center gap-1 rounded border bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50" data-testid={testId}>
      {ok ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />} {ok ? 'Copied' : 'Copy'}
    </button>
  );
};

const Draft = ({ post, onPosted }) => {
  const [lang, setLang] = useState('en');
  const t = (post.texts || {})[lang] || {};
  const tabs = LANGS.map(([k, label]) => (
    <button key={k} type="button" onClick={() => setLang(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${lang === k ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`} data-testid={`gbp-lang-${post.id}-${k}`}>{label}</button>
  ));
  const photos = (post.photos || []).map((p, i) => (
    <a key={i} href={abs(p.url)} target="_blank" rel="noreferrer" className="block group" data-testid={`gbp-photo-${post.id}-${i}`}>
      <img src={abs(p.url)} alt="" className="w-full h-20 object-cover rounded-lg border group-hover:opacity-90" loading="lazy" />
      <div className="text-[10px] text-gray-500 mt-1 line-clamp-2">{p.caption}</div>
    </a>
  ));
  return (
    <div className="rounded-xl border p-4 bg-white" data-testid={`gbp-post-${post.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-bold text-gray-900">{post.month}{post.posted_at && <span className="ml-2 text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">posted</span>}</div>
        <div className="flex gap-1">{tabs}</div>
      </div>
      <div className="mt-3 flex items-start justify-between gap-2">
        <div className="font-semibold text-gray-900 text-sm" data-testid={`gbp-title-${post.id}`}>{t.title}</div>
        <CopyBtn text={t.title || ''} testId={`gbp-copy-title-${post.id}`} />
      </div>
      <div className="mt-1 flex items-start justify-between gap-2">
        <p className="text-sm text-gray-700 whitespace-pre-line flex-1" data-testid={`gbp-text-${post.id}`}>{t.text}</p>
        <CopyBtn text={t.text || ''} testId={`gbp-copy-text-${post.id}`} />
      </div>
      <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">{photos}</div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <a href="https://business.google.com/posts" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-[#1a73e8] text-white font-semibold px-3 py-1.5" data-testid={`gbp-open-${post.id}`}>Open Google Business Profile <ExternalLink className="w-3 h-3" /></a>
        {!post.posted_at && <button type="button" onClick={() => onPosted(post.id)} className="rounded-full border px-3 py-1.5 font-semibold text-gray-700" data-testid={`gbp-mark-posted-${post.id}`}>Mark as posted</button>}
      </div>
    </div>
  );
};

export const GbpPostsCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = () => apiCall('/api/admin/gbp-posts').then(setData).catch((e) => setErr(e.message || 'Failed'));
  useEffect(() => { load(); }, []);
  const generate = async () => {
    setBusy(true); setErr('');
    try { await apiCall('/api/admin/gbp-posts/generate', { method: 'POST' }); await load(); } catch (e) { setErr(e.message || 'Draft failed'); } finally { setBusy(false); }
  };
  const posted = async (id) => { await apiCall(`/api/admin/gbp-posts/${id}/posted`, { method: 'POST' }); load(); };
  const items = (data?.items || []).map((p) => <Draft key={p.id} post={p} onPosted={posted} />);
  const hasThisMonth = !!data?.items?.find((p) => p.id === data.this_month);
  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="gbp-posts-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Megaphone className="w-4 h-4 text-orange-500" /> Monthly Google Business Profile post</h3>
          <p className="text-xs text-gray-500 mt-0.5">Drafted automatically on the 1st (07:30) from fresh Booking.com quotes, the active deal and the month's season — in English, Albanian and Montenegrin, with 5 photos. Copy, paste into GBP → Posts, done.</p>
        </div>
        <button type="button" onClick={generate} disabled={busy || data?.llm === false} className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50" data-testid="gbp-generate">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {hasThisMonth ? 'Redraft this month' : 'Draft this month now'}
        </button>
      </div>
      {err && <p className="text-xs text-red-700" data-testid="gbp-error">{err}</p>}
      {data && !items.length && <p className="text-xs text-gray-500" data-testid="gbp-empty">No drafts yet — press "Draft this month now".</p>}
      {items}
    </div>
  );
};

export default GbpPostsCard;
